import React, { useState, useEffect, useContext, useCallback } from 'react'
import { Button } from 'primereact/button'
import { Message } from 'primereact/message'
import { Tag } from 'primereact/tag'
import server from '../../server'
import Modals from '../shared/Modals'
import { AuthenticationContext } from '../../Auth/authentication.context'

// Single super-admin (webmaster) allowed to use the AS2/EDI troubleshooting tool.
// Kept in sync with the backend `superAdminEmail` allow-list.
export const SUPERADMIN_EMAIL = 'webmaster@orthoplexsolutions.com'

// Map our backend severity vocabulary onto PrimeReact's Message severities.
const SEVERITY_MAP = {
  success: 'success',
  info: 'info',
  warning: 'warn',
  error: 'error',
}

const toMessageSeverity = (severity) => SEVERITY_MAP[severity] || 'info'

const formatDateTime = (value) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

const YesNo = ({ value }) => (
  <Tag
    value={value ? 'Yes' : 'No'}
    severity={value ? 'success' : undefined}
    rounded
  />
)

const EventRow = ({ event }) => {
  const typeSeverity = event.type === 'MDN' ? 'info' : 'warning'
  return (
    <div className='border rounded p-2 mb-2'>
      <div className='d-flex align-items-center flex-wrap gap-2'>
        <Tag value={event.type} severity={typeSeverity} />
        <Tag value={event.status} rounded />
        <span className='text-muted small'>
          {formatDateTime(event.createdAt)}
        </span>
      </div>
      <details className='mt-2'>
        <summary
          style={{ cursor: 'pointer' }}
          className='small text-primary'
        >
          View raw payload
        </summary>
        <pre
          className='mt-2 p-2 bg-light border rounded small'
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            maxHeight: '240px',
            overflow: 'auto',
          }}
        >
          {event.content || event.contentPreview || 'No content recorded.'}
        </pre>
      </details>
    </div>
  )
}

// Map the AS2 health-check status vocabulary onto PrimeReact Tag severities.
const CHECK_TAG_SEVERITY = { ok: 'success', warn: 'warning', fail: 'danger' }

const ManufacturerReport = ({ report, orderId, onChanged }) => {
  const { diagnosis, as2, events } = report

  const [actionLoading, setActionLoading] = useState(null) // 'replacement' | 'new' | 'check' | 'recheck' | null
  const [actionMessage, setActionMessage] = useState(null) // { severity, text }
  const [checkResult, setCheckResult] = useState(null) // array of checks

  // Two ways to re-send, because only the operator can know which is right.
  // A resend happens when no acknowledgement came back, which either means the
  // manufacturer never received the order, or that only the receipt went
  // missing. Sending a replacement for a purchase order they never got can be
  // rejected; sending a new one for an order they already hold gives them two.
  // Ask the manufacturer, then pick.
  const resendEdi = (mode) => {
    const isReplacement = mode === 'replacement'
    const confirmed = window.confirm(
      isReplacement
        ? 'Send this order again as a REPLACEMENT?\n\n' +
            'Use this when the manufacturer may already have the order. It ' +
            'carries the same purchase order number, so they update what they ' +
            'hold instead of booking a second order.'
        : 'Send this order again as a NEW purchase order?\n\n' +
            'Only use this when the manufacturer has confirmed they never ' +
            'received it. If they do already have it, they will end up with ' +
            'two orders.'
    )
    if (!confirmed) return

    setActionLoading(mode)
    setActionMessage(null)
    server
      .post(`/as2-troubleshooting/orders/${orderId}/resend-edi`, {
        manufacturerId: report.id,
        mode,
      })
      .then((response) => {
        setActionMessage({
          severity: response.data?.sent === false ? 'warning' : 'success',
          text:
            response.data?.message || 'EDI 850 resend request completed.',
        })
        setActionLoading(null)
        // Refresh the diagnostics so the new EDI850 event appears in the timeline.
        if (onChanged) onChanged()
      })
      .catch((err) => {
        setActionMessage({
          severity: 'error',
          text:
            err?.response?.data?.message ||
            'Failed to resend the EDI for this manufacturer.',
        })
        setActionLoading(null)
      })
  }

  // Looks on the AS2 server for an acknowledgement this order was never
  // credited with. Reads only — nothing is transmitted — so unlike a resend it
  // cannot place a second purchase order. This is the right first thing to try
  // on anything stuck Pending.
  const recheckMdn = () => {
    setActionLoading('recheck')
    setActionMessage(null)
    server
      .post(`/as2-troubleshooting/orders/${orderId}/recheck-mdn`, {})
      .then((response) => {
        const { found, resolved } = response.data || {}
        setActionMessage({
          severity: resolved ? 'success' : found ? 'info' : 'warning',
          text: response.data?.message || 'Re-check completed.',
        })
        setActionLoading(null)
        if (onChanged) onChanged()
      })
      .catch((err) => {
        setActionMessage({
          severity: 'error',
          text:
            err?.response?.data?.message ||
            'Failed to re-check for the acknowledgement.',
        })
        setActionLoading(null)
      })
  }

  const checkAs2 = () => {
    setActionLoading('check')
    setActionMessage(null)
    server
      .get(`/as2-troubleshooting/orders/${orderId}/check-as2`, {
        params: { manufacturerId: report.id },
      })
      .then((response) => {
        const result = (response.data?.results || [])[0]
        setCheckResult(result ? result.checks : [])
        setActionLoading(null)
      })
      .catch((err) => {
        setActionMessage({
          severity: 'error',
          text:
            err?.response?.data?.message ||
            'Failed to run the AS2 check for this manufacturer.',
        })
        setActionLoading(null)
      })
  }

  return (
    <div className='card mb-3 p-3'>
      <div className='d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2'>
        <div>
          <h6 className='mb-1'>{report.name}</h6>
          <span className='text-muted small'>
            Partner ID: {report.partnerId || '—'}
          </span>
        </div>
        <div className='d-flex gap-2'>
          <Button
            type='button'
            size='small'
            icon='pi pi-replay'
            label='Resend as replacement'
            tooltip='They may already have this order. Sends the same purchase order number so they update it instead of creating a second one.'
            tooltipOptions={{ position: 'bottom' }}
            outlined
            loading={actionLoading === 'replacement'}
            disabled={actionLoading !== null}
            onClick={() => resendEdi('replacement')}
          />
          <Button
            type='button'
            size='small'
            icon='pi pi-send'
            label='Send as new order'
            tooltip='Only when the manufacturer has confirmed they never received it. Sends as a brand new purchase order.'
            tooltipOptions={{ position: 'bottom' }}
            severity='warning'
            outlined
            loading={actionLoading === 'new'}
            disabled={actionLoading !== null}
            onClick={() => resendEdi('new')}
          />
          <Button
            type='button'
            size='small'
            icon='pi pi-search'
            label='Check AS2'
            severity='secondary'
            outlined
            loading={actionLoading === 'check'}
            disabled={actionLoading !== null}
            onClick={checkAs2}
          />
          <Button
            type='button'
            size='small'
            icon='pi pi-inbox'
            label='Look for MDN'
            tooltip='Searches the AS2 server for an acknowledgement this order was never credited with. Reads only — nothing is sent to the manufacturer. Try this before resending.'
            tooltipOptions={{ position: 'bottom' }}
            severity='secondary'
            outlined
            loading={actionLoading === 'recheck'}
            disabled={actionLoading !== null}
            onClick={recheckMdn}
          />
        </div>
      </div>

      {actionMessage && (
        <div className='mb-2'>
          <Message
            severity={toMessageSeverity(actionMessage.severity)}
            text={actionMessage.text}
            style={{ width: '100%', justifyContent: 'flex-start' }}
          />
        </div>
      )}

      {checkResult && (
        <div className='border rounded p-2 mb-2'>
          <div className='small fw-bold mb-2'>AS2 check results</div>
          {checkResult.length === 0 ? (
            <span className='text-muted small'>No checks were returned.</span>
          ) : (
            checkResult.map((check, idx) => (
              <div
                key={idx}
                className='d-flex align-items-start gap-2 mb-1 small'
              >
                <Tag
                  value={check.status.toUpperCase()}
                  severity={CHECK_TAG_SEVERITY[check.status]}
                />
                <span>
                  <strong>{check.label}:</strong> {check.detail}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      <Message
        severity={toMessageSeverity(diagnosis.severity)}
        style={{ width: '100%', justifyContent: 'flex-start' }}
        content={
          <div className='text-start'>
            <strong>{diagnosis.title}</strong>
            <div className='small mt-1'>{diagnosis.message}</div>
            {diagnosis.recommendation && (
              <div className='small mt-2'>
                <strong>Suggested action: </strong>
                {diagnosis.recommendation}
              </div>
            )}
          </div>
        }
      />

      <div className='d-flex flex-wrap gap-3 mt-3 mb-2 small'>
        <span>
          Connection active: <YesNo value={as2.connectionActive} />
        </span>
        <span>
          Production: <YesNo value={as2.production} />
        </span>
        <span>
          Test mode: <YesNo value={as2.ediTest} />
        </span>
        <span className='text-muted'>Directory: {as2.directory || '—'}</span>
      </div>

      <div className='mt-2'>
        <div className='small fw-bold mb-2'>
          AS2 / EDI events ({events.length})
        </div>
        {events.length === 0 ? (
          <span className='text-muted small'>
            No EDI 850 or MDN records were logged for this manufacturer.
          </span>
        ) : (
          events.map((event) => <EventRow key={event.id} event={event} />)
        )}
      </div>
    </div>
  )
}

export default function AS2TroubleshootingButton({ orderId, supplierOrderNo }) {
  const { user } = useContext(AuthenticationContext)
  const isSuperadmin =
    String(user?.email || '').trim().toLowerCase() === SUPERADMIN_EMAIL

  const [eligible, setEligible] = useState(false)
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [diagnostics, setDiagnostics] = useState(null)

  // Decide whether the order has any AS2-integrated manufacturer worth showing
  // the button for. Only ever called for the webmaster account.
  useEffect(() => {
    let active = true
    if (!isSuperadmin || !orderId) return undefined

    server
      .get(`/as2-troubleshooting/orders/${orderId}/eligibility`)
      .then((response) => {
        if (active) setEligible(Boolean(response.data?.eligible))
      })
      .catch(() => {
        if (active) setEligible(false)
      })

    return () => {
      active = false
    }
  }, [isSuperadmin, orderId])

  const fetchDiagnostics = useCallback(() => {
    setLoading(true)
    setError(null)
    server
      .get(`/as2-troubleshooting/orders/${orderId}`)
      .then((response) => {
        setDiagnostics(response.data)
        setLoading(false)
      })
      .catch((err) => {
        setError(
          err?.response?.data?.message ||
            'Unable to load AS2/EDI diagnostics for this order.'
        )
        setLoading(false)
      })
  }, [orderId])

  const openModal = () => {
    setVisible(true)
    fetchDiagnostics()
  }

  if (!isSuperadmin || !eligible) return null

  return (
    <>
      <div className='d-flex justify-content-end mb-2'>
        <Button
          type='button'
          icon='pi pi-wrench'
          label='Troubleshoot AS2 / EDI'
          severity='help'
          outlined
          onClick={openModal}
        />
      </div>

      <Modals
        ModalHeader={`AS2 / EDI Troubleshooting — Order #${
          supplierOrderNo || orderId
        }`}
        btnName='Close'
        visible={visible}
        setVisible={setVisible}
        onButtonClick={() => setVisible(false)}
        width='60rem'
      >
        {loading ? (
          <div className='text-center my-4'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
            <p className='mt-2'>Loading AS2/EDI diagnostics...</p>
          </div>
        ) : error ? (
          <Message severity='error' text={error} />
        ) : diagnostics ? (
          <div>
            <div className='mb-3'>
              <Message
                severity={toMessageSeverity(diagnostics.summary?.severity)}
                style={{ width: '100%', justifyContent: 'flex-start' }}
                content={
                  <div className='text-start'>
                    <strong>Overall: </strong>
                    {diagnostics.summary?.message}
                    <div className='small mt-1'>
                      Order status:{' '}
                      <strong>{diagnostics.order?.status}</strong>
                      {diagnostics.order?.statusSetAt && (
                        <>
                          {' '}
                          (since{' '}
                          {formatDateTime(diagnostics.order.statusSetAt)})
                        </>
                      )}
                    </div>
                  </div>
                }
              />
            </div>

            <div className='d-flex flex-wrap gap-3 mb-3 small text-muted'>
              <span>Pharmacy: {diagnostics.order?.pharmacy?.name || '—'}</span>
              <span>Supplier: {diagnostics.order?.supplier?.name || '—'}</span>
              <span>System: {diagnostics.order?.system || '—'}</span>
            </div>

            {(diagnostics.manufacturers || []).length === 0 ? (
              <Message
                severity='info'
                text='No manufacturers were found for this order.'
              />
            ) : (
              diagnostics.manufacturers.map((report) => (
                <ManufacturerReport
                  key={report.id}
                  report={report}
                  orderId={orderId}
                  onChanged={fetchDiagnostics}
                />
              ))
            )}
          </div>
        ) : null}
      </Modals>
    </>
  )
}
